import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import { segment, withQuery, type SaveVariables } from "@/api/query";
import { financeKeys, type SpendPurpose } from "@/api/finance";
import { reportKeys, type MoneyTotal } from "@/api/reports";

/* Mirrors api/src/pbo_invoicing/cash/schemas.py and the cash and budget reports. Money is
   strings ("1400.00"); nothing is converted at a guessed rate. */

export type AccountKind = "bank" | "cash" | "person" | "sister_company" | "other";
export const ACCOUNT_KINDS: { value: AccountKind; label: string }[] = [
  { value: "bank", label: "Bank account" },
  { value: "cash", label: "Cash" },
  { value: "person", label: "Person (owes or is owed)" },
  { value: "sister_company", label: "Sister company" },
  { value: "other", label: "Other" },
];

export type TransferBasis =
  "own_account" | "sister_settlement" | "reimbursement" | "loan" | "capital" | "other";
export const TRANSFER_BASES: { value: TransferBasis; label: string }[] = [
  { value: "own_account", label: "Between our own accounts" },
  { value: "sister_settlement", label: "Settlement with the sister company" },
  { value: "reimbursement", label: "Reimbursing a person" },
  { value: "loan", label: "Loan" },
  { value: "capital", label: "Capital" },
  { value: "other", label: "Other" },
];
export type TransferStatus = "pending" | "completed";

export type MoneyAccountInput = {
  name: string;
  kind: AccountKind;
  currency: string;
  bank_account_id: string | null;
  opening_balance: string;
  opening_on: string | null;
  is_default_in: boolean;
  is_default_out: boolean;
};

export type MoneyAccount = MoneyAccountInput & {
  id: string;
  /** The ledger account this money account stands for in the books. */
  ledger_account_id: string | null;
  archived_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
};

export function pickAccountInput(account: MoneyAccount): MoneyAccountInput {
  const { name, kind, currency, bank_account_id, opening_balance, opening_on } = account;
  return {
    name,
    kind,
    currency,
    bank_account_id,
    opening_balance,
    opening_on,
    is_default_in: account.is_default_in,
    is_default_out: account.is_default_out,
  };
}

export type TransferInput = {
  made_on: string;
  from_account_id: string;
  to_account_id: string;
  amount: string;
  deductions: string;
  /** In the to-account's currency; null between two accounts of the same currency. */
  received_amount: string | null;
  fx_rate: string | null;
  basis: TransferBasis;
  reference: string;
  notes: string;
  status: TransferStatus;
};

export type Transfer = Omit<TransferInput, "received_amount"> & {
  id: string;
  from_account_name: string;
  to_account_name: string;
  currency: string;
  received_currency: string;
  received_amount: string;
  voided_at: string | null;
  voided_by_username: string | null;
  void_reason: string | null;
  created_by_username: string | null;
  version: number;
  created_at: string;
};

export type TransferFilters = {
  since?: string;
  until?: string;
  accountId?: string;
  includeVoided?: boolean;
};

export type AccountBalance = {
  id: string;
  name: string;
  kind: AccountKind;
  currency: string;
  opening: string;
  received: string;
  transfers_in: string;
  transfers_out: string;
  spent: string;
  balance: string;
  pending_in: string;
  pending_out: string;
};

export type CashReport = {
  as_of: string;
  accounts: AccountBalance[];
  totals: MoneyTotal[];
  held_at_sister: MoneyTotal[];
  owed_to_directors: MoneyTotal[];
  pending_transfers: number;
  not_converted: MoneyTotal[];
  unplaced_payments: number;
  unplaced_expenses: number;
};

export type BudgetStatus = "no_limit" | "within" | "near" | "over" | "fund_short";

/** A bucket's month. `fund_inr` is its share of everything received so far less everything
 *  spent from it so far; negative means spending ahead of the client paying. */
export type BudgetLine = {
  bucket_id: string;
  name: string;
  share_percent: string;
  limit_inr: string | null;
  spent_inr: string;
  left_inr: string | null;
  over: boolean;
  near: boolean;
  fund_inr: string;
  status: BudgetStatus;
};

export type BudgetMonth = { month: string; label: string };

/** What a bucket spent in each month of the financial year; null for a month not begun. */
export type BudgetGridRow = {
  bucket_id: string;
  name: string;
  limit_inr: string | null;
  months: (string | null)[];
};

export type PurposeSpend = {
  purpose: SpendPurpose | null;
  month_inr: string;
  fy_inr: string;
  entries: number;
};

export type BudgetReport = {
  month: string;
  label: string;
  planned_income_inr: string | null;
  lines: BudgetLine[];
  over: string[];
  fund_short: string[];
  grid_months: BudgetMonth[];
  grid: BudgetGridRow[];
  by_purpose: PurposeSpend[];
};

/** A bill a person paid for the company. `amount` is in the person account's currency (null
 *  when it cannot be placed there); `repaid` is what repayments cover, oldest bill first. */
export type OwedBill = {
  expense_id: string;
  version: number;
  spent_on: string;
  description: string;
  payee: string;
  currency: string;
  bill_amount: string;
  amount: string | null;
  repaid: string;
  left: string;
  reimbursed_on: string | null;
};

export type Repayment = {
  transfer_id: string;
  made_on: string;
  from_account_name: string;
  amount: string;
  basis: TransferBasis;
  reference: string;
};

/** `owed` is the negative part of the person account's balance: what PBO owes them. An
 *  archived account shows while something is open, and is restored before reimbursing. */
export type PersonOwed = {
  account_id: string;
  name: string;
  currency: string;
  archived: boolean;
  balance: string;
  owed: string;
  bills: OwedBill[];
  repayments: Repayment[];
};

export type OwedReport = { as_of: string; people: PersonOwed[]; total: MoneyTotal[] };

/** Paying a person back: `amount` in the from-account's currency, `received_amount` in the
 *  person's, needed only when the two differ. */
export type ReimburseInput = {
  made_on: string;
  from_account_id: string;
  amount: string;
  received_amount: string | null;
  reference: string;
  notes: string;
};

export const cashKeys = {
  owed: ["reports", "owed"] as const,
  allAccounts: ["money-accounts"] as const,
  accounts: (includeArchived: boolean) => ["money-accounts", includeArchived] as const,
  allTransfers: ["transfers"] as const,
  transfers: (filters: TransferFilters) => ["transfers", filters] as const,
  cash: (asOf: string | undefined) => ["reports", "cash", asOf ?? "today"] as const,
  budget: (month: string | undefined) => ["reports", "budget", month ?? "current"] as const,
};

// --------------------------------------------------------------------------- requests

export function listMoneyAccounts(includeArchived = false): Promise<MoneyAccount[]> {
  return apiJson<MoneyAccount[]>(
    withQuery("/money-accounts", { include_archived: includeArchived }),
  );
}

export function saveMoneyAccount({
  input,
  existing,
}: SaveVariables<MoneyAccountInput>): Promise<MoneyAccount> {
  return existing
    ? apiSend<MoneyAccount>(
        `/money-accounts/${segment(existing.id)}`,
        "PUT",
        input,
        existing.version,
      )
    : apiSend<MoneyAccount>("/money-accounts", "POST", input);
}

export function setAccountArchived(
  id: string,
  archived: boolean,
  version: number,
): Promise<MoneyAccount> {
  const action = archived ? "archive" : "unarchive";
  return apiSend<MoneyAccount>(
    `/money-accounts/${segment(id)}/${action}`,
    "POST",
    undefined,
    version,
  );
}

export function listTransfers(filters: TransferFilters): Promise<Transfer[]> {
  return apiJson<Transfer[]>(
    withQuery("/transfers", {
      since: filters.since,
      until: filters.until,
      account_id: filters.accountId,
      include_voided: filters.includeVoided,
    }),
  );
}

export function recordTransfer(input: TransferInput): Promise<Transfer> {
  return apiSend<Transfer>("/transfers", "POST", input);
}

export function setTransferStatus(
  id: string,
  status: TransferStatus,
  version: number,
): Promise<Transfer> {
  return apiSend<Transfer>(`/transfers/${segment(id)}`, "PUT", { status }, version);
}

export function voidTransfer(id: string, reason: string, version: number): Promise<Transfer> {
  return apiSend<Transfer>(`/transfers/${segment(id)}/void`, "POST", { reason }, version);
}

export function getCashReport(asOf?: string): Promise<CashReport> {
  return apiJson<CashReport>(withQuery("/reports/cash", { as_of: asOf }));
}

export function getBudget(month?: string): Promise<BudgetReport> {
  return apiJson<BudgetReport>(withQuery("/reports/budget", { month }));
}

export function getOwed(): Promise<OwedReport> {
  return apiJson<OwedReport>("/reports/owed");
}

export function reimburse(accountId: string, input: ReimburseInput): Promise<PersonOwed> {
  return apiSend<PersonOwed>(`/money-accounts/${segment(accountId)}/reimburse`, "POST", input);
}

// --------------------------------------------------------------------------- hooks

export function useMoneyAccounts(includeArchived = false) {
  return useQuery({
    queryKey: cashKeys.accounts(includeArchived),
    queryFn: () => listMoneyAccounts(includeArchived),
  });
}

export function useTransfers(filters: TransferFilters) {
  return useQuery({ queryKey: cashKeys.transfers(filters), queryFn: () => listTransfers(filters) });
}

export function useCashReport(asOf?: string) {
  return useQuery({ queryKey: cashKeys.cash(asOf), queryFn: () => getCashReport(asOf) });
}

export function useBudget(month?: string) {
  return useQuery({ queryKey: cashKeys.budget(month), queryFn: () => getBudget(month) });
}

export function useOwed() {
  return useQuery({ queryKey: cashKeys.owed, queryFn: getOwed });
}

/** Anything that moves money changes balances, the dashboard and the reports; a repayment
 *  also marks the bills it covers. */
function useCashMutation<Variables, Result>(mutationFn: (variables: Variables) => Promise<Result>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all(
        [cashKeys.allAccounts, cashKeys.allTransfers, financeKeys.allExpenses, reportKeys.all].map(
          (queryKey) => queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  });
}

export function useReimburse() {
  return useCashMutation(({ accountId, input }: { accountId: string; input: ReimburseInput }) =>
    reimburse(accountId, input),
  );
}

export function useSaveAccount() {
  return useCashMutation(saveMoneyAccount);
}

export function useSetAccountArchived() {
  return useCashMutation(
    ({ id, archived, version }: { id: string; archived: boolean; version: number }) =>
      setAccountArchived(id, archived, version),
  );
}

export function useRecordTransfer() {
  return useCashMutation(recordTransfer);
}

export function useSetTransferStatus() {
  return useCashMutation(
    ({ id, status, version }: { id: string; status: TransferStatus; version: number }) =>
      setTransferStatus(id, status, version),
  );
}

export function useVoidTransfer() {
  return useCashMutation(
    ({ id, reason, version }: { id: string; reason: string; version: number }) =>
      voidTransfer(id, reason, version),
  );
}
